/**
 * Theme Configuration - FutJá Admin Panel
 *
 * Paleta de cores e configurações de tema seguindo a identidade visual do app
 */

const themeDefault = {
	type: 'dark',
	font: {
		family: {
			primary: 'Inter, system-ui, -apple-system, sans-serif',
			mono: 'source-code-pro, Menlo, Monaco, Consolas, monospace',
		},
		size: {
			xs: '0.75rem',   // 12px
			sm: '0.875rem',  // 14px
			base: '1rem',    // 16px
			lg: '1.125rem',  // 18px
			xl: '1.25rem',   // 20px
			'2xl': '1.5rem', // 24px
			'3xl': '1.875rem', // 30px
			'4xl': '2.25rem',  // 36px
		},
		weight: {
			normal: 400,
			medium: 500,
			semibold: 600,
			bold: 700,
		},
	},
	spacing: {
		xs: '0.25rem',  // 4px
		sm: '0.5rem',   // 8px
		md: '1rem',     // 16px
		lg: '1.5rem',   // 24px
		xl: '2rem',     // 32px
		'2xl': '3rem',  // 48px
	},
	borderRadius: {
		sm: '0.25rem',
		md: '0.5rem',
		lg: '0.75rem',
		xl: '1rem',
		full: '9999px',
	},
	shadows: {
		sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
		md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
		lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
		xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
	},
}

/**
 * Dark Theme - Tema principal do FutJá
 */
export const darkTheme = {
	...themeDefault,
	type: 'dark',
	colors: {
		// Cores principais
		primary: '#31A362',
		primaryLight: '#59ba81',
		primaryDark: '#257f4f',
		secondary: '#D58936',
		secondaryLight: '#efa957',
		secondaryDark: '#a86829',
		accent: '#F2F3AE',

		// Texto
		text: '#F9FAFB',
		textBody: '#9D9D9D',
		textMuted: '#6B7280',

		// Backgrounds
		base100: '#0F1110', // Fundo principal
		base200: '#474A48', // Cinza médio
		base300: '#1F2120', // Fundo secundário
		base400: '#2D2F2E', // Fundo terciário

		// Status
		success: '#5CBD27',
		successLight: '#87EC23',
		warning: '#EAC652',
		warningLight: '#ECCC23',
		info: '#4A90E2',
		danger: '#EA5252',
		dangerLight: '#ec2323',

		// Especiais
		gold: '#EEA04A',
		silver: '#939393',
		bronze: '#985111',

		// Borders
		border: '#374151',
		borderLight: '#4B5563',
	},
}

/**
 * Light Theme - Tema claro alternativo
 */
export const lightTheme = {
	...themeDefault,
	type: 'light',
	colors: {
		// Cores principais
		primary: '#31A362',
		primaryLight: '#59ba81',
		primaryDark: '#257f4f',
		secondary: '#F16E05',
		secondaryLight: '#ff8c33',
		secondaryDark: '#c45a04',
		accent: '#F2F3AE',

		// Texto
		text: '#28282D',
		textBody: '#858588',
		textMuted: '#9CA3AF',

		// Backgrounds
		base100: '#FFFFFF', // Fundo principal
		base200: '#C0C0C0', // Cinza médio
		base300: '#F2F2F2', // Fundo secundário
		base400: '#D1D0D1', // Fundo terciário

		// Status
		success: '#87EC23',
		successLight: '#a8f54d',
		warning: '#ECCC23',
		warningLight: '#f5dc4d',
		info: '#4A90E2',
		danger: '#ec2323',
		dangerLight: '#ff4d4d',

		// Especiais
		gold: '#EEA04A',
		silver: '#939393',
		bronze: '#985111',

		// Borders
		border: '#E5E7EB',
		borderLight: '#F3F4F6',
	},
}

/**
 * Função helper para obter variáveis CSS do tema
 */
export const getCSSVariables = (theme) => ({
	'--color-primary': theme.colors.primary,
	'--color-primary-light': theme.colors.primaryLight,
	'--color-primary-dark': theme.colors.primaryDark,
	'--color-secondary': theme.colors.secondary,
	'--color-secondary-light': theme.colors.secondaryLight,
	'--color-secondary-dark': theme.colors.secondaryDark,
	'--color-accent': theme.colors.accent,
	'--color-text': theme.colors.text,
	'--color-text-body': theme.colors.textBody,
	'--color-text-muted': theme.colors.textMuted,
	'--color-base-100': theme.colors.base100,
	'--color-base-200': theme.colors.base200,
	'--color-base-300': theme.colors.base300,
	'--color-base-400': theme.colors.base400,
	'--color-success': theme.colors.success,
	'--color-warning': theme.colors.warning,
	'--color-info': theme.colors.info,
	'--color-danger': theme.colors.danger,
	'--color-border': theme.colors.border,
})

const themeExports = { darkTheme, lightTheme, getCSSVariables }

export default themeExports
